import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import toggleLike from 'hb-react/shared/components/DesignSystem/Reactions/utils/toggleLike';
import FilePreviewModal from 'hb-react/shared/components/FilePreview';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import { isCommentsModuleAvailable } from 'hb-react/shared/utils/features/comments';
import { setFileTypeArticleOrFile } from 'hb-react/shared/utils/files/fileContentTypes';

import { ContentThumbnail } from './ContentThumbnail';

interface SharedContentThumbnailProps {
  fromRecord: {
    id: number;
    url: string;
    name: string;
    content_type: string;
    file_type: string;
    thumbnail_url: string;
    shared_from: string;
    shared_url: string;
    type: string;
    extension: string;
  };
}

const SharedContentThumbnail = ({
  fromRecord,
}: SharedContentThumbnailProps) => {
  const { currentUser, currentNetwork } = useDefaultAppContexts();

  const [previewOpen, setPreviewOpen] = useState(false);

  const fileUuid = fromRecord?.shared_url?.split('/')?.pop();
  const { data: filePreview, isLoading: filePreviewLoading } = useQuery({
    queryKey: ['filePreview', fileUuid],
    queryFn: () =>
      axios
        .get(Routes.show_file_portfolios_v2_index_path(), {
          params: {
            network_id: currentNetwork.id,
            uuid: fileUuid,
          },
        })
        .then(({ data }) => data.file || data.article),
    enabled: !!fileUuid && previewOpen,
  });

  const showReactions = isCommentsModuleAvailable(
    currentUser,
    !currentNetwork?.network_configuration?.portfolio?.comments_enabled,
  );

  return (
    <>
      <ContentThumbnail
        content={{
          url: fromRecord.url,
          name: fromRecord.name,
          content_type: fromRecord.content_type,
          file_type: fromRecord.file_type,
          thumbnail_url: fromRecord.thumbnail_url,
          cover_picture_file_url: fromRecord.thumbnail_url,
          type: fromRecord.type,
          extension: fromRecord.extension,
        }}
        sharedFrom={fromRecord.shared_from}
        sharedUrl={fromRecord.shared_url}
        onClick={() => fileUuid && setPreviewOpen(true)}
      />
      {previewOpen && (
        <FilePreviewModal
          isOpen={previewOpen}
          onCloseModal={() => setPreviewOpen(false)}
          screenReadersCloseButtonText={s__(
            'Live Feed|Close file preview modal',
          )}
          content={{ hidePreviousUrl: true, hideNextUrl: true }}
          file={
            filePreview && {
              ...filePreview,
              sharePostUrl: fromRecord.shared_url,
            }
          }
          isLoading={filePreviewLoading}
          reactions={{
            type: setFileTypeArticleOrFile(filePreview?.type),
            railsType: setFileTypeArticleOrFile(filePreview?.type),
            initialLikes: filePreview?.likes,
            initialTotalComments: filePreview?.total_comments,
            isOpenInModal: true,
            onTogglePostLike: toggleLike,
            id: filePreview?.id,
            displayCommentsByDefault: true,
          }}
          showReactions={showReactions}
        />
      )}
    </>
  );
};

export default SharedContentThumbnail;
